import React, { FC, useEffect, useMemo } from "react";
import { Form, FormikConfig, FormikProvider, useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { RenderFormFields } from "src/components/RenderFormFields";
import { selectIndustries } from "src/redux/industries/selectors";
import { selectCloneTypes } from "src/redux/cloneTypes/selectors";
import { selectSoldInformations } from "src/redux/soldInformations/selectors";
import { getSoldInformationsAsync } from "src/redux/soldInformations/actions";
import { selectCountries } from "src/redux/countries/selectors";
import { getCloneEditCreateFields } from "./constants/fields";
import { ActionButtons } from "./ActionButtons";
import { ICloneSettingsFormValues } from "./types";

interface Props {
  formikProps: FormikConfig<ICloneSettingsFormValues>;
  firstTitle: string;
  secondTitle: string;
}

export const CloneForm: FC<Props> = ({
  formikProps,
  firstTitle,
  secondTitle,
}) => {
  const dispatch = useAppDispatch();

  const { items: cloneTypes } = useAppSelector(selectCloneTypes);
  const { data: industries } = useAppSelector(selectIndustries);
  const { items: soldInformations } = useAppSelector(selectSoldInformations);
  const { items: countries } = useAppSelector(selectCountries);

  const cloneEditCreateFields = useMemo(() => {
    const fields = getCloneEditCreateFields(
      cloneTypes,
      industries,
      soldInformations,
      countries
    );
    return {
      first: fields.slice(0, 1),
      rest: fields.slice(1),
    };
  }, [industries, cloneTypes, soldInformations, countries]);

  const formik = useFormik<ICloneSettingsFormValues>(formikProps);

  useEffect(() => {
    dispatch(getSoldInformationsAsync());
  }, [dispatch]);

  return (
    <FormikProvider value={formik}>
      <Form className="flex flex-col gap-10">
        <div className="flex flex-col gap-5 xl:gap-10">
          <h1>{firstTitle}</h1>
          <RenderFormFields fields={cloneEditCreateFields.first} />
        </div>

        <div className="flex flex-col gap-5 xl:gap-8">
          <h2 className="text-28 sm:text-40">{secondTitle}</h2>
          <div className="flex flex-col gap-5 xl:gap-8">
            <RenderFormFields fields={cloneEditCreateFields.rest} />
          </div>
        </div>
        <ActionButtons />
      </Form>
    </FormikProvider>
  );
};
